import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShoppingBag, Package, ArrowUpRight, Shirt, Tag, Plus } from 'lucide-react';
import { toast } from 'sonner';
import ItemAdditionsChart from '@/components/dashboard/ItemAdditionsChart';
import UserAdditionsChart from '@/components/dashboard/UserAdditionsChart';
import TopCategoriesChart from '@/components/dashboard/TopCategoriesChart';
import { TimeRange } from '@/services/analyticsService';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsMobile } from '@/hooks/use-mobile';
import { getProxiedImageUrl } from '@/services/imageProxyService';
import { Database } from '@/integrations/supabase/types';

type ClothingRow = Database['public']['Tables']['db']['Row'];

type BaseItem = {
  _id: number;
  name: string;
  image: string;
  price: number;
  brand: string;
  batch: string;
  "category[0]": string;
  "category[1]": string;
  "category[2]": string;
  created_at: string;
  created_by: string;
  link: string;
  updated_at: string;
};

interface RecentItem extends BaseItem {
  type: 'clothing';
  itemUrl: string;
}

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalItems: 0,
    clothingCategories: [] as {name: string, value: number}[]
  });
  const [recentItems, setRecentItems] = useState<RecentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<TimeRange>('7d');
  const isMobile = useIsMobile();
  const [proxiedImages, setProxiedImages] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { count: totalClothing } = await supabase
          .from('db')
          .select('*', { count: 'exact', head: true });

        const { data: recentClothing, error: recentClothingError } = await supabase
          .from('db')
          .select('*')
          .order('_id', { ascending: false })
          .limit(6);
          
        if (recentClothingError) {
          console.error('Error fetching recent clothing items:', recentClothingError);
          toast.error('Failed to load recent clothing items');
          return;
        }
          
        console.log('Recent clothing items:', recentClothing);

        setStats({
          totalItems: totalClothing || 0,
          clothingCategories: []
        });

        // Format recent items
        const formattedRecentItems = recentClothing?.map(item => ({
          ...item,
          type: 'clothing' as const,
          itemUrl: `/admin/clothinglist/${item._id}`
        })) || [];
        
        setRecentItems(formattedRecentItems);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast.error('Failed to load dashboard data');
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  useEffect(() => {
    const proxyImages = async () => {
      const newProxiedImages: { [key: string]: string } = {};
      
      for (const item of recentItems) {
        if (item.image) {
          try {
            const proxiedUrl = await getProxiedImageUrl(item.image);
            newProxiedImages[item.image] = proxiedUrl;
          } catch (error) {
            console.error('Error proxying image:', error);
          }
        }
      }
      
      setProxiedImages(prev => ({ ...prev, ...newProxiedImages }));
    };
    
    if (recentItems.length > 0) {
      proxyImages();
    }
  }, [recentItems]);

  return (
    <div className="space-y-6 w-full">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
        <h1 className="text-2xl font-bold mb-2 md:mb-0">Dashboard</h1>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
          <Link to="/admin/clothinglist/new">
            <Button className="flex items-center gap-2">
              <Plus size={16} />
              Add Product
            </Button>
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="grid place-items-center h-40">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle>Products Summary</CardTitle>
                <CardDescription>Overview of products in the system</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-4">
                  <div className="flex items-center justify-between p-4 rounded-lg bg-primary/10">
                    <div className="flex items-center">
                      <div className="mr-4 rounded-full bg-primary/20 p-2">
                        <ShoppingBag className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Total Products</p>
                        <p className="text-2xl font-bold">{stats.totalItems}</p>
                      </div>
                    </div>
                    <ArrowUpRight className="h-6 w-6 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <Card className="overflow-hidden">
              <CardHeader>
                <CardTitle>Recent Products</CardTitle>
                <CardDescription>Latest products added to the system</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y max-h-[400px] overflow-y-auto">
                  {recentItems.length === 0 ? (
                    <div className="p-6 text-center text-muted-foreground">
                      No products found
                    </div>
                  ) : (
                    recentItems.map((item) => (
                      <Link 
                        key={`${item.type}-${item._id}`} 
                        to={item.itemUrl}
                        className="flex items-center hover:bg-muted/50 p-4 transition-colors"
                      >
                        <div className="h-12 w-12 rounded-md overflow-hidden flex-shrink-0 bg-muted mr-4">
                          {item.image ? (
                            <img 
                              src={proxiedImages[item.image] || item.image} 
                              alt={item.name}
                              className="h-full w-full object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = 'https://placehold.co/100x100/png';
                              }}
                            />
                          ) : (
                            <div className="h-full w-full flex items-center justify-center bg-muted">
                              <Package className="h-6 w-6 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{item.name}</p>
                          <div className="flex items-center mt-1">
                            <Badge variant="outline" className="mr-2 text-xs">
                              {item.brand}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              ${item.price?.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))
                  )}
                </div>
              </CardContent>
              <CardFooter className="border-t p-4">
                <Button asChild variant="outline" className="w-full">
                  <Link to="/admin/clothinglist">
                    View All Products
                  </Link>
                </Button>
              </CardFooter>
            </Card>

            <Card className="overflow-hidden">
              <CardHeader>
                <CardTitle>Top Categories</CardTitle>
                <CardDescription>Most popular product categories overall</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <TopCategoriesChart />
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Activity Analytics</CardTitle>
              <CardDescription>Product additions over time</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ItemAdditionsChart timeRange={timeRange} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>User Activity</CardTitle>
              <CardDescription>Items added by each user</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <UserAdditionsChart timeRange={timeRange} />
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
